import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { StyleSheet, View, type ViewStyle } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { AddressCopiedToast } from './AddressCopiedToast'

// How long the pill stays fully shown, and the duration of its exit animation
// (kept in sync with AddressCopiedToast's FadeOutUp) used to defer the overlay
// teardown until the fade-out finishes.
const TOAST_VISIBLE_MS = 1800
const TOAST_EXIT_MS = 350
const TOAST_INSET_OFFSET = 20

// Full-screen, top-anchored, non-interactive overlay.
// pointerEvents="none" on the View lets all touches pass through.
const toastOverlay: ViewStyle = {
  ...StyleSheet.absoluteFill,
  zIndex: 1,
  alignItems: 'center',
}

interface CopiedToast {
  /** The overlay to render; caller owns only where it sits in the tree. */
  toast: ReactNode
  showCopiedToast: (label?: string) => void
  clearToast: () => void
}

/**
 * The "Address copied" toast: its timing state and the overlay that shows it.
 *
 * Two pieces of state: `copiedToastShown` drives the pill's mount (its
 * reanimated enter/exit plays on mount/unmount), and `toastMounted` keeps
 * the overlay mounted across the pill's exit animation so the fade-out
 * isn't cut off by tearing the View down on the same frame.
 */
export function useCopiedToast(): CopiedToast {
  const insets = useSafeAreaInsets()
  const [label, setLabel] = useState<string>()
  const [toastMounted, setToastMounted] = useState(false)
  const [copiedToastShown, setCopiedToastShown] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const clearToast = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
    }
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current)
    }
    setLabel(undefined)
    setCopiedToastShown(false)
    setToastMounted(false)
  }, [])

  const showCopiedToast = useCallback((nextLabel?: string) => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
    }
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current)
    }
    setLabel(nextLabel)
    setToastMounted(true)
    setCopiedToastShown(true)
    hideTimerRef.current = setTimeout(() => {
      setCopiedToastShown(false)
      unmountTimerRef.current = setTimeout(
        () => setToastMounted(false),
        TOAST_EXIT_MS,
      )
    }, TOAST_VISIBLE_MS)
  }, [])

  useEffect(() => () => clearToast(), [clearToast])

  const toast = toastMounted ? (
    <View
      style={[toastOverlay, { paddingTop: insets.top + TOAST_INSET_OFFSET }]}
      pointerEvents="none"
    >
      {copiedToastShown ? <AddressCopiedToast label={label} /> : null}
    </View>
  ) : null

  return { toast, showCopiedToast, clearToast }
}
